import { css } from "@emotion/css";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { KubernetesMonitorStatus } from "@octopusdeploy/octopus-server-client";
import classNames from "classnames";
import React from "react";
import ExternalLink from "~/components/Navigation/ExternalLink";
interface MonitorStatusProps {
    healthStatus: KubernetesMonitorStatus;
    name: string;
}
export const MonitorStatus: React.FC<MonitorStatusProps> = ({ healthStatus, name }) => {
    const getStatusInfo = (status: KubernetesMonitorStatus) => {
        switch (status) {
            case KubernetesMonitorStatus.Online:
                return {
                    boxStyle: styles.success,
                    icon: <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>,
                    statusText: "healthy",
                    showHelpLink: false,
                };
            case KubernetesMonitorStatus.Offline:
                return {
                    boxStyle: styles.failure,
                    icon: <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>,
                    statusText: "unhealthy",
                    showHelpLink: true,
                };
            case KubernetesMonitorStatus.NotRegistered:
            default:
                return {
                    boxStyle: styles.failure,
                    icon: <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>,
                    statusText: "not registered",
                    showHelpLink: true,
                };
        }
    };
    const { boxStyle, icon, statusText, showHelpLink } = getStatusInfo(healthStatus);
    return (<div className={classNames(styles.box, boxStyle)} role="status">
            <div className={styles.contentContainer}>
                <div className={styles.iconContainer}>{icon}</div>
                <span>
                    Kubernetes Monitor for <b>{name}</b> is {statusText}
                    {showHelpLink && (<>
                            . For help, <ExternalLink href={"kubernetes-monitor-installation-help"}>read the docs</ExternalLink>
                        </>)}
                </span>
            </div>
        </div>);
};
MonitorStatus.displayName = "MonitorStatus"
const styles = {
    box: css({
        padding: `${space[12]} ${space[16]}`,
        alignSelf: "stretch",
        display: "grid",
        marginTop: space[16],
        fontSize: fontSize.medium,
        color: themeTokens.color.text.primary,
        borderRadius: borderRadius.small,
    }),
    success: css({
        backgroundColor: themeTokens.color.callout.background.success.default,
    }),
    failure: css({
        backgroundColor: themeTokens.color.callout.background.danger.default,
    }),
    contentContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        "&:not(:last-child)": {
            marginBottom: space[16],
        },
    }),
    iconContainer: css({
        padding: `${space[4]} ${space[2]} 0 ${space[2]}`,
        marginRight: space[4],
        width: "24px",
    }),
};
